// Подпись подложки в СКАЧАННОМ кадре.
//
// `composeFrame` рисует холст MapLibre, сетку и плашку; подпись поверх карты —
// HTML-оверлей, и в PNG, MP4 и ZIP он не попадает никак. Кадр с картографией
// Яндекса без знака — нарушение на материале, который уходит из кабинета
// наружу: в отчёт, в переписку, в презентацию. Поэтому рисуем прямо в кадр,
// тем же приёмом, что и плашку экспорта.

import type { BasemapSpec } from '../basemap'
import { t } from '../../i18n'
import { yandexLogoSvg } from './attributionView'

/** Нужна ли для подписи картинка (её надо декодировать заранее). */
export function needsAttributionImage(spec: BasemapSpec): boolean {
  return spec.attribution.kind === 'logo'
}

export interface Placement {
  x: number
  y: number
  scale: number
}

/** Базовая ширина кадра, на которой знак идёт в натуральную величину.
 *  Кадр кабинета обычно шире, ретиновый — вдвое, и знак, положенный
 *  константой, на них становился бы всё мельче до нечитаемости. */
const BASE_WIDTH = 1200

/** Куда и в каком масштабе класть подпись.
 *
 *  В ЛЕВЫЙ низ: правый нижний занят плашкой экспорта (`drawPlate` кладёт её на
 *  `width - box - 12`), и подпись легла бы под неё.
 *
 *  Прижата к краю без отступа: у знака Яндекса поля уже внутри картинки, и
 *  условия прямо просят своих не добавлять. */
export function attributionPlacement(
  width: number,
  height: number,
  size: { width: number; height: number },
): Placement {
  // Потолок в четверть кадра: на маленькой картинке знак не должен спорить с
  // самой картой, а нижний предел держит его читаемым.
  const raw = width / BASE_WIDTH
  const maxScale = width / 4 / size.width
  const scale = Math.max(0.5, Math.min(raw, maxScale))
  return { x: 0, y: height - size.height * scale, scale }
}

/** Знак подложки как картинка. Декодируется ЗАРАНЕЕ: `composeFrame` синхронен,
 *  а ролик зовёт его на каждый кадр — ждать там нечего и некогда. */
let ready: { key: string; image: CanvasImageSource; width: number; height: number } | null = null

export async function prepareAttribution(spec: BasemapSpec, lang: string): Promise<void> {
  if (!needsAttributionImage(spec)) {
    ready = null
    return
  }
  const key = `${spec.provider}:${lang}`
  if (ready?.key === key) return
  const svg = yandexLogoSvg(lang)
  // data: URL, а не blob и не сетевой адрес: кадр уходит в `toBlob`, и
  // изображение из чужого источника пометило бы холст «испорченным» (tainted),
  // после чего снимок стал бы невозможен вовсе.
  const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
  const image = new Image()
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve()
    image.onerror = () => reject(new Error('basemap attribution failed to decode'))
    image.src = url
  })
  ready = { key, image, width: image.width || 88, height: image.height || 48 }
}

/** Нарисовать подпись в кадр. Синхронна — зовётся из `composeFrame`. */
export function drawAttribution(
  ctx: CanvasRenderingContext2D,
  spec: BasemapSpec,
  width: number,
  height: number,
): void {
  ctx.save()
  if (spec.attribution.kind === 'logo') {
    // Не готова — кадр всё равно уйдёт: молча не подписать хуже, чем подписать
    // строкой, поэтому падаем на текст, а не пропускаем подпись.
    if (ready) {
      const p = attributionPlacement(width, height, ready)
      ctx.drawImage(ready.image, p.x, p.y, ready.width * p.scale, ready.height * p.scale)
      ctx.restore()
      return
    }
  }
  // Запасная подпись видима человеку — значит из каталога, а не литералом.
  // Строка Carto, наоборот, литерал намеренно: это формулировка правообладателей.
  const text =
    spec.attribution.kind === 'text'
      ? spec.attribution.text
      : `© ${t(spec.attribution.altKey)}`
  const pad = 6
  ctx.font = '12px system-ui, sans-serif'
  ctx.textBaseline = 'bottom'
  const w = ctx.measureText(text).width
  ctx.fillStyle = 'rgba(255,255,255,0.72)'
  ctx.fillRect(0, height - 18, w + pad * 2, 18)
  ctx.fillStyle = '#1f2937'
  ctx.fillText(text, pad, height - 4)
  ctx.restore()
}
